import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { TopBarTool } from "./TopBarTool"
import * as Icons from "@nova/component-icons"
import { selectActiveToolName, setActiveTool } from "plugins/Toolbox/tool-registry"
import { selectSnapshotOpen, setSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { sidePanelMinWidth, ToolName } from "@nova/constants"
import { CreationMenu } from "features/content-overlay/creation-menu/CreationMenu"
import { useEffect } from "react"
import { TopBarUploadTool } from "./TopBarUploadTool"
import { selectWatching, unsubscribeToMap } from "features/pubsub/slice"
import { usePluginState } from "panels/content/side-panel/usePluginState"
import { getToolboxPlugins } from "plugins/plugin-registry"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { setInspectorPanelState } from "features/popups/slice"

export const TopBarToolBar = () => {
    const dispatch = useAppDispatch()
    const activeTool = useAppSelector(selectActiveToolName())
    const pdfSnapshotIsOpen = useAppSelector(selectSnapshotOpen())
    const { currentPlugin, setSelectedPlugin } = usePluginState()
    const isSubscribedToUser = !!useAppSelector(selectWatching())

    const isEditor = useIsEditor()

    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement

            // Ignore key if it was pressed for a different reason.
            if (
                target.tagName === "INPUT" ||
                target.tagName === "TEXTAREA" ||
                target.isContentEditable
            )
                return

            if (e.ctrlKey || e.metaKey) return

            let tool: ToolName | undefined
            switch (e.key.toLowerCase()) {
                case "escape":
                    dispatch(setActiveTool(undefined))
                    dispatch(setInspectorPanelState("active"))
                    return
                case "m":
                    if (isSubscribedToUser) dispatch(unsubscribeToMap())
                    if (!pdfSnapshotIsOpen) {
                        if (currentPlugin) setSelectedPlugin(undefined)
                        if (activeTool) dispatch(setActiveTool(undefined))
                        dispatch(setSnapshotOpen(true))
                        dispatch(setActiveTool(ToolName.PDF))
                    } else {
                        dispatch(setSnapshotOpen(false))
                        dispatch(setActiveTool(undefined))
                    }
                    return
                case "p":
                    if (isEditor) tool = ToolName.Icon
                    break
                case "l":
                    if (isEditor) tool = ToolName.Line
                    break
                case "t":
                    if (isEditor) tool = ToolName.Text
                    break
                case "r":
                    if (isEditor) tool = ToolName.Ruler
                    break
                case "c":
                    if (isEditor) tool = ToolName.Comment
                    break
                case "u":
                    if (isEditor) tool = ToolName.Upload
                    break
                default:
                    return
            }

            if (isSubscribedToUser) dispatch(unsubscribeToMap())
            dispatch(setActiveTool(tool && tool === activeTool ? undefined : tool))
        }

        document.addEventListener("keydown", handleKeydown)

        return () => {
            document.removeEventListener("keydown", handleKeydown)
        }
    }, [
        activeTool,
        isEditor,
        pdfSnapshotIsOpen,
        currentPlugin,
        dispatch,
        setSelectedPlugin,
        isSubscribedToUser,
    ])

    return (
        <div className="flex-[2_2_0%] h-full flex items-center justify-center gap-1">
            {isEditor ? (
                <>
                    {getToolboxPlugins().map((tool) => (
                        <TopBarTool {...tool} key={tool.name} />
                    ))}
                    <TopBarUploadTool />

                    <div className="relative">
                        <TopBarTool icon={Icons.Widgets} name={ToolName.Create} />
                        {activeTool === ToolName.Create && (
                            <CreationMenu
                                shouldIncludeTools={false}
                                onBlur={() => dispatch(setActiveTool(undefined))}
                                onMouseLeave={() => dispatch(setActiveTool(undefined))}
                                className="absolute top-full -left-7"
                                style={{ width: `calc(${sidePanelMinWidth}px - 1.5rem)` }}
                            />
                        )}
                    </div>
                </>
            ) : (
                <TopBarTool icon={Icons.Map} name={ToolName.PDF} shortcut={{ letters: ["M"] }} />
            )}
        </div>
    )
}
